/**
 * walkClaimText (RV pack, RV5) — extends walkCitationText to also
 * parse inline reliability tokens (`[verified ...]`, `[inferred ...]`,
 * `[unverified ...]`) emitted by the LLM and render a
 * <ReliabilityBadge/> next to the citation.
 *
 * Compared to walkCitationText:
 *   - Splits on a combined regex that matches BOTH `[Ref N]` and the
 *     three reliability token shapes (see claimReliability.ts).
 *   - ReliabilityBadge omits visual reliability labels while retaining
 *     free-text qualifications and reliability metadata. Citation-bearing
 *     tokens always retain their <RefLink/> independently of the badge.
 *
 * Renderers can call `walkClaimText` in place of `walkCitationText`
 * anywhere they want per-claim reliability surfaced. When the LLM
 * hasn't emitted reliability tokens (legacy reports, degraded mode),
 * walkClaimText degrades to identical output to walkCitationText.
 */
import React from 'react';
import {
  normalizeCompoundRefTokens,
  stripUnresolvableRefTokens,
  removeOutOfBoundsRefTokens,
  ensureSpaceAfterRefTokens,
} from './citationTokens';
import { stripNarrativeArtifacts } from './reportRendering/narrativeArtifacts';
import { presentReportReliabilityTokens } from './reportRendering/presentReportReliabilityTokens';
import type { DataSource } from '../types/siteRag';
import { RefLink } from '../components/siterag/report/RefLink';
import { ReliabilityBadge } from '../components/siterag/report/ReliabilityBadge';
import {
  CLAIM_SPLIT_RE,
  isPlainRefToken,
  extractRefN,
  parseReliabilityToken,
} from './claimReliability';
import { isEditorial2ColEnabled } from './runtimeFlags';

export function walkClaimText(
  text: string | undefined | null,
  refs: DataSource[] | undefined,
  appendixVisibleIndices?: Set<number> | null,
): React.ReactNode[] {
  if (!text) return [];
  const list = refs ?? [];
  // A token is out-of-bounds when it leaves the array OR (when the
  // caller supplies the appendix-visibility set) when its appendix row
  // was filtered out — keeping the in-text marker consistent with the
  // appendix. A null/undefined set means "no appendix constraint".
  const isOob = (n: number): boolean =>
    n < 1 || n > list.length || (appendixVisibleIndices ? !appendixVisibleIndices.has(n) : false);
  // Redhead wave 3 — (defect 1) strip legacy persisted `[Ref ?]`/`[?]`
  // placeholders AND remove out-of-bounds `[Ref N]` tokens textually
  // (removed + logged — never rendered as a literal `[?]` pill, and no
  // doubled space left behind); (defect 6) repair the missing space after a
  // surviving citation token ("[Ref 2].A" → "[Ref 2]. A") before the split.
  const resolveDocUrlToRef = (rawUrl: string): number | undefined => {
    const trimmed = rawUrl.trim();
    const idMatch = trimmed.match(/^doc:\/\/([^/?#\s]+)/i);
    const targetId = idMatch?.[1]?.toLowerCase();
    for (let idx = 0; idx < list.length; idx++) {
      const r = list[idx];
      if (!r) continue;
      if (r.url && r.url.trim().toLowerCase() === trimmed.toLowerCase()) return idx + 1;
      if (targetId && r.url && r.url.toLowerCase().includes(targetId)) return idx + 1;
      if (targetId && r.field && r.field.toLowerCase().includes(targetId)) return idx + 1;
    }
    return undefined;
  };
  const preResolvedDocUrls = text
    .replace(/\s*\[source passage\]\(\s*(doc:\/\/(?!sim\/)[^\s)]+)\s*\)/gi, (_m, rawUrl: string) => {
      const n = resolveDocUrlToRef(rawUrl);
      return n !== undefined ? ` [Ref ${n}]` : '';
    })
    .replace(/\s*\[source passage\]/gi, '')
    .replace(
      /\(\s*(doc:\/\/(?!sim\/)[^\s(),;]+(?:\s*[,;]\s*doc:\/\/(?!sim\/)[^\s(),;]+)*)\s*\)/gi,
      (_m, urlList: string) => {
        const resolved = urlList
          .split(/\s*[,;]\s*/)
          .map((u) => resolveDocUrlToRef(u))
          .filter((n): n is number => n !== undefined);
        const unique = [...new Set(resolved)];
        return unique.length > 0 ? unique.map((n) => `[Ref ${n}]`).join(', ') : '';
      },
    )
    .replace(/\(\s*(doc:\/\/(?!sim\/)[^\s(),;]+)\s*,\s*([^()]+)\)/gi, (_m, rawUrl: string, rest: string) => {
      const n = resolveDocUrlToRef(rawUrl);
      return `(${rest.trim()})${n !== undefined ? ` [Ref ${n}]` : ''}`;
    })
    .replace(/\bdoc:\/\/(?!sim\/)[^\s)\],;"']*/gi, (rawUrl: string) => {
      const n = resolveDocUrlToRef(rawUrl);
      return n !== undefined ? `[Ref ${n}]` : '';
    });
  const normalized = ensureSpaceAfterRefTokens(
    removeOutOfBoundsRefTokens(
      stripUnresolvableRefTokens(
        // Normalize display labels before compound expansion so mixed tagged
        // groups still receive the same per-reference bounds/visibility checks.
        normalizeCompoundRefTokens(presentReportReliabilityTokens(stripNarrativeArtifacts(preResolvedDocUrls))),
        'walkClaimText',
      ),
      isOob,
      'walkClaimText',
    ),
  )
    // SENTENCE-SPACING (William 2026-07-04) — repair a missing space between two
    // run-together sentences the generator emitted ("option.Call notes" →
    // "option. Call notes"; "…viable [Ref 13].The low" → "…]. The low").
    // `splitSentences` (citationDensity) only breaks on period+WHITESPACE, so a
    // no-space boundary is never split and never re-spaced. DISPLAY-ONLY: claim
    // matching + citation density run on the RAW text upstream (walkClaimsText),
    // before this. Guarded to lowercase/digit/`]`/`)` + `.` + UPPERCASE so
    // decimals (1.5), section numbers, acronyms (U.S., a.m.-before-lowercase),
    // and adjacent ref tokens (`].[`) are untouched.
    .replace(/([a-z0-9\])])\.([A-Z])/g, '$1. $2');
  // The regex carries the global flag, but `.split` with a stateful regex
  // can leak its lastIndex across calls — clone it per call.
  const splitter = new RegExp(CLAIM_SPLIT_RE.source, 'gi');
  const parts = normalized.split(splitter);

  /** Render ONE split fragment: an inline [Ref N] pill, a reliability badge
   *  (+ optional pill), or a plain-text run. Pure on the fragment + refs. */
  const renderPart = (part: string, i: number): React.ReactNode => {
    if (!part) return part;
    if (isPlainRefToken(part)) {
      const n = extractRefN(part);
      const outOfBounds = isOob(n);
      return <RefLink key={i} n={n} reference={list[n - 1]} outOfBounds={outOfBounds} />;
    }
    const tok = parseReliabilityToken(part);
    if (tok) {
      if (tok.refN !== undefined) {
        const outOfBounds = isOob(tok.refN);
        return (
          <React.Fragment key={i}>
            <ReliabilityBadge reliability={tok.reliability} qualifier={tok.qualifier} />
            <RefLink n={tok.refN} reference={list[tok.refN - 1]} outOfBounds={outOfBounds} />
          </React.Fragment>
        );
      }
      return <ReliabilityBadge key={i} reliability={tok.reliability} qualifier={tok.qualifier} />;
    }
    return part;
  };

  // Default (editorial TWO-COLUMN body OFF — the byte-stable path everywhere by
  // default): one node per fragment, IDENTICAL to the historical output.
  if (!isEditorial2ColEnabled()) {
    return parts.map(renderPart);
  }

  // Editorial TWO-COLUMN body ON — keep each inline [Ref N] pill GLUED to the
  // trailing sentence/clause punctuation that immediately follows it, so a
  // narrow justified column never breaks a lone "." (or "," / ";" / ":" / "!" /
  // "?" / ")") to the top of the next column as an orphan. The pill itself is
  // already `whitespace-nowrap`, but the punctuation is a SEPARATE text
  // fragment; wrapping the pill + that leading punctuation in one `nowrap` span
  // makes the pair unbreakable. Non-citation fragments render exactly as before,
  // so the on-page prose is otherwise unchanged.
  const LEADING_PUNCT_RE = /^[.,;:!?)]+/;
  const out: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]!;
    const node = renderPart(part, i);
    const isCitationPill =
      isPlainRefToken(part) || parseReliabilityToken(part)?.refN !== undefined;
    const next = parts[i + 1];
    const punctMatch =
      isCitationPill && typeof next === 'string' ? next.match(LEADING_PUNCT_RE) : null;
    if (punctMatch) {
      const punct = punctMatch[0];
      out.push(
        <span key={`nw-${i}`} style={{ whiteSpace: 'nowrap' }}>
          {node}
          {punct}
        </span>,
      );
      // Consume the glued punctuation so the following fragment doesn't
      // re-render it (empty remainder renders as '' — same as any empty part).
      parts[i + 1] = next.slice(punct.length);
    } else {
      out.push(node);
    }
  }
  return out;
}
